import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import apiClient from '../../../services/apiService';
import { supplierRequestService, getErrorMessage } from '../../../services/apiService';

const UploadSignedDocument = ({ requestId, onBack, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    if (!file) {
      setError('Please select a file');
      return false;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Only PDF, JPEG, and PNG files are allowed');
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = (file) => {
    setError(null);

    if (!validateFile(file)) {
      return;
    }

    setSelectedFile(file);

    // Generate preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Upload the approval document
      const formData = new FormData();
      formData.append('plant_head_approval', selectedFile);

      await apiClient.post(`/supplier-requests/${requestId}/upload-approval-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Step 2: Submit the request for approval
      await supplierRequestService.submitRequest(requestId);

      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to upload and submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueLater = () => {
    // Simply go back - the draft is already saved
    onBack();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Button variant="outline" onClick={onBack}>
            <Icon name="ArrowLeft" size={18} />
            Back to Form
          </Button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border p-8">
          {/* Draft Saved Notice */}
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-green-900">Your draft has been saved!</p>
                <p className="text-sm text-green-700 mt-1">
                  You can safely exit and come back anytime to upload the signed document. Your progress is saved.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
              Upload Signed Document
            </h1>
            <p className="text-gray-600 mt-2">Upload the Plant Head approved and signed request form</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400'
            }`}
            onDrop={handleDrop}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
          >
            {selectedFile ? (
              <div className="space-y-4">
                {preview ? (
                  <div className="max-w-md mx-auto">
                    <img src={preview} alt="Preview" className="rounded-lg shadow-md" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Icon name="FileText" size={64} className="text-blue-600" />
                  </div>
                )}
                
                <div>
                  <p className="font-semibold text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-600">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreview(null);
                  }}
                >
                  <Icon name="X" size={16} />
                  Remove
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <Icon name="Upload" size={64} className="text-gray-400" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-700 mb-2">
                    Drop your file here or click to browse
                  </p>
                  <p className="text-sm text-gray-500">
                    Supported formats: PDF, JPEG, PNG (Max 10MB)
                  </p>
                </div>
                <div>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleFileSelect(e.target.files[0]);
                      }
                    }}
                  />
                  <label 
                    htmlFor="file-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 border-2 border-gray-300 hover:border-blue-500 bg-white hover:bg-gray-50 text-gray-700 hover:text-blue-700 rounded-lg font-medium cursor-pointer transition-all duration-200"
                  >
                    <Icon name="FolderOpen" size={18} />
                    Browse Files
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Icon name="Info" size={18} />
              Upload Instructions
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
              <li>Ensure the document is clearly scanned or photographed</li>
              <li>The signature and stamp must be visible</li>
              <li>File must be in PDF, JPEG, or PNG format</li>
              <li>Maximum file size is 10MB</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex gap-4">
            <Button
              variant="outline"
              onClick={handleContinueLater}
              className="flex-1"
            >
              <Icon name="Save" size={20} />
              Continue Later
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!selectedFile || loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 py-3"
            >
              {loading ? (
                <>
                  <Icon name="Loader" size={20} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Icon name="Send" size={20} />
                  Submit Request
                </>
              )}
            </Button>
          </div>
          
          {/* Help Text */}
          <p className="mt-4 text-center text-sm text-gray-500">
            Click "Continue Later" to save and exit. You can return anytime from your draft requests.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UploadSignedDocument;

